import { useState } from 'react';

import { Reveal } from '@/components/reveal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useLocale } from '@/i18n/locale-context';
import { loginWithEmail, sendEmailCode } from '@/lib/home-auth.service';

type WaitlistStep = 'email' | 'code' | 'success';

export function WaitlistSection() {
  const { t } = useLocale();
  const [step, setStep] = useState<WaitlistStep>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [messageId, setMessageId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSendCode(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await sendEmailCode(email);
      if (!res.messageId) {
        throw new Error(t.waitlist.error);
      }
      setMessageId(res.messageId);
      setCode('');
      setStep('code');
    } catch (err) {
      setError(err instanceof Error ? err.message : t.waitlist.error);
    } finally {
      setLoading(false);
    }
  }

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await loginWithEmail({ email, messageId, code });
      setStep('success');
    } catch (err) {
      setError(err instanceof Error ? err.message : t.waitlist.error);
    } finally {
      setLoading(false);
    }
  }

  async function handleResend() {
    setError(null);
    setLoading(true);
    try {
      const res = await sendEmailCode(email);
      if (!res.messageId) {
        throw new Error(t.waitlist.error);
      }
      setMessageId(res.messageId);
      setCode('');
    } catch (err) {
      setError(err instanceof Error ? err.message : t.waitlist.error);
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="relative mx-auto w-full max-w-7xl px-6 py-24 sm:px-10 sm:py-32">
      <div className="absolute inset-0 -z-10 bg-muted/20" />
      <Reveal>
        <div className="mx-auto max-w-lg text-center">
          <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
            {t.waitlist.eyebrow}
          </p>
          <h2 className="mt-4 text-2xl font-semibold tracking-tight sm:text-3xl">
            {t.waitlist.title}
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            {t.waitlist.description}
          </p>

          {step === 'success' ? (
            <p className="mx-auto mt-8 max-w-md rounded-lg border border-primary/20 bg-primary/10 px-4 py-3 text-sm font-medium text-primary">
              {t.waitlist.success}
            </p>
          ) : step === 'email' ? (
            <form className="mx-auto mt-8 flex max-w-md flex-col gap-3" onSubmit={handleSendCode}>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t.waitlist.placeholder}
                className="h-10 w-full rounded-lg border-border bg-background px-4 text-sm"
                aria-label={t.waitlist.placeholder}
                required
                disabled={loading}
                autoComplete="email"
              />
              <Button
                type="submit"
                className="h-10 w-full rounded-lg px-5 text-sm shadow-[0_0_20px_-6px_hsl(174_72%_48%_/_0.6)]"
                disabled={loading}
              >
                {loading ? t.waitlist.sending : t.waitlist.sendCodeButton}
              </Button>
            </form>
          ) : (
            <form className="mx-auto mt-8 flex max-w-md flex-col gap-3" onSubmit={handleVerify}>
              <Input
                type="email"
                value={email}
                readOnly
                className="h-10 w-full rounded-lg border-border bg-muted/40 px-4 text-sm text-muted-foreground"
                aria-label={t.waitlist.placeholder}
              />
              <Input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder={t.waitlist.codePlaceholder}
                className="h-10 w-full rounded-lg border-border bg-background px-4 text-sm"
                aria-label={t.waitlist.codePlaceholder}
                required
                disabled={loading}
              />
              <Button
                type="submit"
                className="h-10 w-full rounded-lg px-5 text-sm shadow-[0_0_20px_-6px_hsl(174_72%_48%_/_0.6)]"
                disabled={loading}
              >
                {loading ? t.waitlist.verifying : t.waitlist.verifyButton}
              </Button>
              <button
                type="button"
                onClick={handleResend}
                disabled={loading}
                className="text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline disabled:opacity-50"
              >
                {t.waitlist.resendCode}
              </button>
            </form>
          )}

          {error && step !== 'success' ? (
            <p className="mx-auto mt-4 max-w-md text-sm text-destructive" role="alert">
              {error}
            </p>
          ) : null}
        </div>
      </Reveal>
    </section>
  );
}
